// Build Your Own Hamper JavaScript
// Handles all functionality for the multi-step hamper builder


const BuildYourOwnHamper = {
    // State Management
    state: {
        currentStep: 1,
        selectedBox: null,
        selectedItems: [], // Will be set automatically
        personalisations: {},
        messaging: {
            giftTag: false,
            giftTagTo: '',
            giftTagFrom: '',
            giftMessage: '',
            boxPersonalisation: '',
            boxInsideMessage: '',
            boxOutsideMessage: ''
        }
    },

    // Initialize
    init() {
        this.cacheDOMElements();
        this.bindEvents();

        // Check if we're editing an existing hamper from cart
        this.checkForEditingHamperData();

        // Set up cleanup when navigating away from page
        this.setupNavigationCleanup();

        // Load preselected products from Liquid JSON
        const preselectedJson = document.getElementById('preselected-products-json');
    if (preselectedJson) {
        try {
            const preselectedProducts = JSON.parse(preselectedJson.textContent);
            this.state.selectedItems = preselectedProducts.map(prod => ({
                id: prod.id,
                title: prod.title,
                price: prod.price ? parseFloat(prod.price) : 0,
                comparePrice: prod.compare_at_price ? parseFloat(prod.compare_at_price) : null,
                available: true,
                images: prod.featured_image ? [{ src: prod.featured_image }] : [],
                tags: prod.tags || [],
                vendor: prod.vendor || '',
                type: prod.product_type || '',
                variants: prod.variants || [],
                quantity: 1, // Default quantity
                // Add these lines:
                canPersonalise: (prod.tags || []).some(tag => tag.toLowerCase().startsWith('personalise:') || tag.toLowerCase() === 'personalise'),
                personalisationType: this.getPersonalisationType(prod.tags || [])
            }));
        } catch (e) {
            console.error('Failed to parse preselected products JSON', e);
        }
    }

    this.state.currentStep = 1;
    this.updateUI();
    
    // Update button text if in editing mode
    if (this.state.isEditingMode) {
        setTimeout(() => {
            this.updateButtonText();
        }, 1000);
    }
},

    // Check for editing hamper data from cart
    checkForEditingHamperData() {
        try {
            const editingDataJson = localStorage.getItem('editingHamperData');
            
            if (editingDataJson) {
                const editingData = JSON.parse(editingDataJson);
                
                // Store in hamper state for future use
                this.state.editingHamperData = editingData;
                this.state.isEditingMode = true;
                this.state.originalHamperId = editingData.hamperId;
                
                console.log('🔄 Editing mode activated for hamper ID:', editingData.hamperId);
                
                // Auto-select the box from the editing data
                this.autoSelectBoxFromEditingData(editingData);
                
                // Clear the localStorage data after using it to prevent interference with other pages
                setTimeout(() => {
                    localStorage.removeItem('editingHamperData');
                }, 5000); // Clear after 5 seconds to allow all auto-population to complete
            }
            
        } catch (error) {
            console.error('Error loading editing hamper data:', error);
        }
    },

    // Auto-select box from editing data
    autoSelectBoxFromEditingData(editingData) {
        // Get box selection from personalisations data
        if (!editingData.personalisations || !editingData.personalisations['Box Selection']) {
            return;
        }

        const targetBoxSelection = editingData.personalisations['Box Selection'];
        
        // Extract base box name (remove color variant in parentheses)
        let targetBoxName = targetBoxSelection;
        if (targetBoxSelection.includes('(') && targetBoxSelection.includes(')')) {
            targetBoxName = targetBoxSelection.split('(')[0].trim();
        }

        // Update header text to indicate editing mode
        this.updateHeaderForEditingMode();

        // Wait a bit for DOM to be fully loaded
        setTimeout(() => {
            const boxOptions = document.querySelectorAll('.box-option');
            
            boxOptions.forEach(boxOption => {
                const boxTitleElement = boxOption.querySelector('.box-option-title');
                if (boxTitleElement) {
                    const boxTitle = boxTitleElement.textContent.trim();
                    
                    if (boxTitle === targetBoxName) {
                        // Trigger click to select the box
                        boxOption.click();
                        
                        // Check if this is a Wicker Basket and we need to set color variant
                        setTimeout(() => {
                            this.autoSelectBoxVariant(targetBoxSelection);
                        }, 300);
                        
                        // Store editing data for later use when user navigates to steps manually
                        this.state.pendingEditingData = editingData;
                        
                        return;
                    }
                }
            });
        }, 500); // Small delay to ensure DOM is ready
    },

    // Update header text for editing mode
    updateHeaderForEditingMode() {
        const headerTitle = document.querySelector('.hamper-header h1');
        if (headerTitle && this.state.isEditingMode) {
            headerTitle.textContent = 'Edit Your Hamper';
            console.log('🔄 Header updated for editing mode');
        }
    },

    // Auto-select box variant/color based on box selection string
    autoSelectBoxVariant(boxSelection) {
        // Check if this is a Wicker Basket with color variant
        if (boxSelection.includes('Wicker Basket') && boxSelection.includes('(') && boxSelection.includes(')')) {
            // Extract color from box selection (e.g., "Wicker Basket (Dark)" -> "Dark")
            const colorMatch = boxSelection.match(/\(([^)]+)\)/);
            if (colorMatch) {
                const selectedColor = colorMatch[1].toLowerCase(); // "Dark" -> "dark"
                
                // Find the color dropdown - try multiple selectors
                let colorSelect = document.querySelector('.box-color');
                if (!colorSelect) {
                    colorSelect = document.querySelector('select[name*="color"]');
                }
                if (!colorSelect) {
                    colorSelect = document.querySelector('select[id*="color"]');
                }
                if (!colorSelect) {
                    colorSelect = document.querySelector('.wicker-color-select');
                }
                if (!colorSelect) {
                    colorSelect = document.querySelector('.selected-box select');
                }
                
                if (colorSelect) {
                    // Find the matching option
                    const options = colorSelect.querySelectorAll('option');
                    let matchingOption = null;
                    
                    options.forEach(option => {
                        const optionValue = option.value.toLowerCase();
                        const optionText = option.textContent.toLowerCase();
                        
                        if (optionValue === selectedColor || optionText === selectedColor) {
                            matchingOption = option;
                        }
                    });
                    
                    if (matchingOption) {
                        colorSelect.value = matchingOption.value;
                        
                        // Trigger change event to update the box image and state
                        colorSelect.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                }
            }
        }
    },

    // Auto-populate personalisation data
    autoPopulatePersonalisationData(editingData) {
        if (!editingData.personalisations) {
            return;
        }

        const personalisations = editingData.personalisations;

        // Check for "Your message" field
        if (personalisations['Your message']) {
            const yourMessage = personalisations['Your message'];

            // Find and click the personalisation toggle (this will trigger JS to show input field)
            const toggleSlider = document.querySelector('.toggle-slider');
            
            if (toggleSlider) {
                // Trigger click event to activate the JavaScript functionality
                console.log('� Toggling personalisation option on');
                toggleSlider.click();
                
                // Use separate method to handle field population
                this.populatePersonalisationField(yourMessage);
                setTimeout(() => {
                    // Try multiple selectors for the input field
                    let personalisationField = document.querySelector('input[placeholder="Enter your message..."]');
                    if (!personalisationField) {
                        personalisationField = document.querySelector('.personalisation-field input');
                    }
                    if (!personalisationField) {
                        personalisationField = document.querySelector('textarea[placeholder="Enter your message..."]');
                    }
                    
                    console.log('� Input field found:', !!personalisationField);
                    
                    if (personalisationField) {
                        console.log('�📝 Filling personalisation field with:', yourMessage);
                        personalisationField.value = yourMessage;
                        
                        // Trigger multiple events to ensure the form recognizes the change
                        personalisationField.dispatchEvent(new Event('input', { bubbles: true }));
                        personalisationField.dispatchEvent(new Event('change', { bubbles: true }));
                        
                        console.log('✅ Personalisation field populated successfully!');
                    } else {
                        console.log('❌ Could not find personalisation input field');
                    }
                }, 500); // Wait for toggle animation to complete
            } else {
                console.log('❌ Could not find toggle slider');
            }
        } else {
            console.log('ℹ️ No "Your message" data found, skipping personalisation');
        }
    },

    // Populate personalisation field with proper debugging
    populatePersonalisationField(yourMessage) {
        console.log('DEBUG: Current step before personalisation:', this.state.currentStep);
        
        // Only populate if we're already on step 2 (user navigated there manually)
        if (this.state.currentStep !== 2) {
            console.log('ℹ️ Not on step 2, skipping personalisation auto-population');
            return;
        }
        
        // Wait longer for the personalisation UI to fully load after toggle and step change
        setTimeout(() => {
            console.log('DEBUG: Searching for personalisation input field after toggle...');
            
            // Debug: Check if setupPersonalisation has run
            const personalisationItems = document.querySelector('#personalisation-items');
            if (personalisationItems) {
                console.log('DEBUG: Personalisation items content:', personalisationItems.innerHTML.length > 0 ? 'Has content' : 'Empty');
                console.log('DEBUG: Personalisation items HTML:', personalisationItems.innerHTML.substring(0, 200));
            }
            
            // Try specific selectors based on the personalisation structure
            let personalisationField = null;
            
            // Try 1: Look for input inside personalisation-field (matches the setupPersonalisation structure)
            personalisationField = document.querySelector('.personalisation-field input[type="text"]');
            console.log('Selector 1 - .personalisation-field input: found =', !!personalisationField);
            
            if (!personalisationField) {
                // Try 2: Look for input with data-item-key (from setupPersonalisation method)
                personalisationField = document.querySelector('.personalisation-options input[data-item-key]');
                console.log('Selector 2 - input[data-item-key]: found =', !!personalisationField);
            }
            
            if (!personalisationField) {
                // Try 3: Look for input with placeholder containing "message" within personalisation context
                personalisationField = document.querySelector('.personalisation-item input[placeholder*="message"]');
                console.log('Selector 3 - personalisation input with message placeholder: found =', !!personalisationField);
            }
            
            if (!personalisationField) {
                // Try 3: Look for any input/textarea that appeared after the toggle (exclude search and other existing fields)
                const allFields = document.querySelectorAll('input[type="text"], textarea');
                console.log('DEBUG: Checking', allFields.length, 'text fields...');
                
                for (let field of allFields) {
                    // Skip search fields, cart fields, and other non-personalisation fields
                    if (field.id === 'search-input' || 
                        field.name === 'q' || 
                        field.closest('.cart-drawer') || 
                        field.closest('.search-bar') ||
                        field.closest('.header')) {
                        continue;
                    }
                    
                    // Look for fields that are visible and likely personalisation-related
                    if (field.offsetParent !== null && 
                        (field.placeholder && field.placeholder.toLowerCase().includes('message')) ||
                        field.closest('.personalisation-item') ||
                        field.closest('#personalisation-items')) {
                        personalisationField = field;
                        console.log('DEBUG: Found personalisation field:', field);
                        break;
                    }
                }
            }
            
            console.log('FINAL RESULT: Personalisation field found =', !!personalisationField);
            
            if (personalisationField) {
                console.log('SUCCESS: Filling personalisation field with value:', yourMessage);
                
                // First, find and turn on the associated toggle
                const itemKey = personalisationField.getAttribute('data-item-key');
                if (itemKey) {
                    const toggle = document.querySelector(`input[type="checkbox"][data-item-key="${itemKey}"]`);
                    const personalOptionsContainer = document.querySelector(`.personalisation-options[data-item-key="${itemKey}"]`);
                    
                    if (toggle && !toggle.checked) {
                        console.log('SUCCESS: Turning on toggle for item:', itemKey);
                        toggle.checked = true;
                        
                        // Trigger the change event to activate the personalisation options
                        toggle.dispatchEvent(new Event('change', { bubbles: true }));
                        
                        // Show the personalisation options container
                        if (personalOptionsContainer) {
                            personalOptionsContainer.classList.add('active');
                        }
                    }
                }
                
                // Then populate the input field
                personalisationField.value = yourMessage;
                
                // Trigger events to ensure form recognizes the change
                personalisationField.dispatchEvent(new Event('input', { bubbles: true }));
                personalisationField.dispatchEvent(new Event('change', { bubbles: true }));
                
                // Update character count if it exists
                const charCountElement = personalisationField.parentElement.querySelector('.character-count span');
                if (charCountElement) {
                    charCountElement.textContent = yourMessage.length;
                }
                
                console.log('SUCCESS: Toggle activated and personalisation field populated successfully!');
                
            } else {
                console.log('ERROR: Could not find personalisation input field');
                console.log('ERROR: Personalisation section exists:', !!document.querySelector('#personalisation-items'));
            }
        }, 2500); // Increased timeout to 2.5 seconds for proper loading after step change
    },

    // Auto-populate messaging data in step 3
    autoPopulateMessagingData(editingData) {
        console.log('📨 Auto-populating messaging data...');
        
        if (!editingData || !editingData.personalisations) {
            console.log('❌ No messaging data found');
            return;
        }

        const personalisations = editingData.personalisations;
        console.log('📝 Messaging data to populate:', personalisations);

        // Only populate if we're already on step 3 (user navigated there manually)
        if (this.state.currentStep !== 3) {
            console.log('ℹ️ Not on step 3, skipping messaging auto-population');
            return;
        }

        // Restore wedding card selection if there was one
        this.restoreWeddingCardSelection(editingData);

        setTimeout(() => {
            // 1. Handle Gift Tag fields
            if (personalisations['Gift Tag To'] || personalisations['Gift Tag From'] || personalisations['Gift Message']) {
                console.log('🎁 Populating gift tag data...');
                
                // Find and turn on "Add a Gift Tag?" toggle - try multiple selectors
                let giftTagToggle = document.querySelector('#gift-tag-toggle input[type="checkbox"]');
                console.log('DEBUG: Selector 1 (#gift-tag-toggle input) found:', !!giftTagToggle);
                
                if (!giftTagToggle) {
                    giftTagToggle = document.querySelector('input#gift-tag-toggle');
                    console.log('DEBUG: Selector 2 (input#gift-tag-toggle) found:', !!giftTagToggle);
                }
                
                if (!giftTagToggle) {
                    giftTagToggle = document.querySelector('input[name="gift-tag"], input[id*="gift-tag"]');
                    console.log('DEBUG: Selector 3 (name/id pattern) found:', !!giftTagToggle);
                }
                
                if (!giftTagToggle) {
                    // Look for any checkbox near gift tag fields
                    const giftTagSection = document.querySelector('#gift-tag-to, #gift-tag-from').closest('div').parentElement;
                    if (giftTagSection) {
                        giftTagToggle = giftTagSection.querySelector('input[type="checkbox"]');
                        console.log('DEBUG: Selector 4 (checkbox near gift fields) found:', !!giftTagToggle);
                    }
                }
                
                console.log('DEBUG: Final gift tag toggle found:', !!giftTagToggle);
                console.log('DEBUG: Gift tag toggle checked:', giftTagToggle ? giftTagToggle.checked : 'N/A');
                giftTagToggle.click();
                if (giftTagToggle && !giftTagToggle.checked) {
                    console.log('✅ Enabling gift tag toggle');
                    
                    // Set checked to true
                    giftTagToggle.click();
                    
                    // Trigger the change event to activate the functionality
                    const changeEvent = new Event('change', { bubbles: true });
                    giftTagToggle.dispatchEvent(changeEvent);
                    
                    console.log('✅ Gift tag toggle activated');
                } else if (giftTagToggle && giftTagToggle.checked) {
                    console.log('ℹ️ Gift tag toggle already enabled');
                }

                setTimeout(() => {
                    // Fill gift tag fields
                    if (personalisations['Gift Tag To']) {
                        const giftTagToField = document.querySelector('#gift-tag-to');
                        console.log('DEBUG: Gift Tag To field found:', !!giftTagToField);
                        if (giftTagToField) {
                            giftTagToField.value = personalisations['Gift Tag To'];
                            console.log('✅ Gift Tag To populated:', personalisations['Gift Tag To']);
                        }
                    }

                    if (personalisations['Gift Tag From']) {
                        const giftTagFromField = document.querySelector('#gift-tag-from');
                        console.log('DEBUG: Gift Tag From field found:', !!giftTagFromField);
                        if (giftTagFromField) {
                            giftTagFromField.value = personalisations['Gift Tag From'];
                            console.log('✅ Gift Tag From populated:', personalisations['Gift Tag From']);
                        }
                    }

                    if (personalisations['Gift Message']) {
                        const giftMessageField = document.querySelector('#gift-message');
                        console.log('DEBUG: Gift Message field found:', !!giftMessageField);
                        if (giftMessageField) {
                            giftMessageField.value = personalisations['Gift Message'];
                            // Trigger input event to update character count
                            giftMessageField.dispatchEvent(new Event('input', { bubbles: true }));
                            console.log('✅ Gift Message populated:', personalisations['Gift Message']);
                        }
                    }
                }, 500); // Increased delay to allow toggle activation to complete
            }

            // 2. Handle Box Personalisation
            if (personalisations['Box Personalisation']) {
                console.log('📦 Populating box personalisation...');
                
                const boxPersonalisationSelect = document.querySelector('#box-personalisation');
                if (boxPersonalisationSelect) {
                    const value = personalisations['Box Personalisation'];
                    console.log('📦 Setting box personalisation to:', value);
                    
                    boxPersonalisationSelect.value = value;
                    // Trigger change event to show message fields
                    boxPersonalisationSelect.dispatchEvent(new Event('change', { bubbles: true }));

                    setTimeout(() => {
                        // Fill inside and outside messages
                        if (personalisations['Box Inside Message']) {
                            const insideMessageField = document.querySelector('#box-inside-message');
                            if (insideMessageField) {
                                insideMessageField.value = personalisations['Box Inside Message'];
                                insideMessageField.dispatchEvent(new Event('input', { bubbles: true }));
                                console.log('✅ Box Inside Message populated:', personalisations['Box Inside Message']);
                            }
                        }

                        if (personalisations['Box Outside Message']) {
                            const outsideMessageField = document.querySelector('#box-outside-message');
                            if (outsideMessageField) {
                                outsideMessageField.value = personalisations['Box Outside Message'];
                                outsideMessageField.dispatchEvent(new Event('input', { bubbles: true }));
                                console.log('✅ Box Outside Message populated:', personalisations['Box Outside Message']);
                            }
                        }
                    }, 300);
                }
            }

            console.log('✅ Messaging data population complete!');
        }, 1000); // Wait for step 3 to load
    },

    // Restore wedding card selection from editing data
    restoreWeddingCardSelection(editingData) {
        console.log('🎴 Attempting to restore wedding card selection...');
        console.log('🎴 Full editing data received:', editingData);
        
        if (!editingData.linkedItems || editingData.linkedItems.length === 0) {
            console.log('ℹ️ No linked items found in editing data');
            console.log('ℹ️ editingData.linkedItems:', editingData.linkedItems);
            return;
        }

        console.log('🎴 All linked items in editing data:');
        editingData.linkedItems.forEach((item, index) => {
            console.log(`  Item ${index + 1}:`, {
                title: item.title,
                productType: item.productType,
                vendor: item.vendor,
                variantId: item.variantId,
                isWeddingCard: item.isWeddingCard,
                key: item.key
            });
        });

        // Find wedding card in the linked items - focus on title containing 'Card' or 'card'
        const weddingCard = editingData.linkedItems.find(item => 
            item.isWeddingCard || 
            (item.title && (
              item.title.includes('Card') ||
              item.title.includes('card')
            ))
        );

        if (!weddingCard) {
            console.log('ℹ️ No wedding card found in linked items');
            console.log('ℹ️ Search criteria used: isWeddingCard=true OR title contains "Card" OR title contains "card"');
            console.log('ℹ️ Available items for debugging:');
            editingData.linkedItems.forEach((item, index) => {
                console.log(`  Item ${index + 1} search details:`, {
                    title: item.title?.toLowerCase(),
                    productType: item.productType?.toLowerCase(),
                    vendor: item.vendor?.toLowerCase(),
                    isWeddingCard: item.isWeddingCard
                });
            });
            return;
        }

        console.log('🎴 Found wedding card to restore:');
        console.log('  - Title:', weddingCard.title);
        console.log('  - Product Type:', weddingCard.productType);
        console.log('  - Vendor:', weddingCard.vendor);
        console.log('  - Variant ID:', weddingCard.variantId);
        console.log('  - Is Wedding Card flag:', weddingCard.isWeddingCard);
        console.log('  - Cart Key:', weddingCard.key);
        console.log('  - Full wedding card object:', weddingCard);

        // Wait a bit for the popup to be ready
        setTimeout(() => {
            // Find the wedding card in the popup by variant ID or title
            const slides = document.querySelectorAll('.wedding-cards-swiper .swiper-slide');
            let matchingSlide = null;

            slides.forEach(slide => {
                const variantId = slide.getAttribute('variant-id');
                const title = slide.getAttribute('data-title');
                
                // Try to match by variant ID first, then by title
                if ((weddingCard.variantId && variantId == weddingCard.variantId) ||
                    (weddingCard.title && title && title.toLowerCase() === weddingCard.title.toLowerCase())) {
                    matchingSlide = slide;
                }
            });

            if (matchingSlide) {
                console.log('✅ Found matching wedding card slide, selecting it...');
                
                // Remove any existing active classes
                slides.forEach(s => s.classList.remove('card-active'));
                
                // Add active class to the matching slide
                matchingSlide.classList.add('card-active');
                
                // Enable the add card button
                const addCardBtn = document.getElementById('add-wedding-card-to-cart');
                if (addCardBtn) {
                    addCardBtn.classList.remove('disabled_btn');
                    addCardBtn.click(); // Simulate click to add the card
                    // Find the input element and set checked to true
const giftCardToggle = document.getElementById('gift-card-toggle');
if (giftCardToggle) {
    giftCardToggle.checked = true;
}

let weddingCardsSwiper = null;
 if (!weddingCardsSwiper) {
                weddingCardsSwiper = new Swiper('.wedding-cards-swiper', {
                    slidesPerView: 2,
                    navigation: {
                        nextEl: '.card-button-next',
                        prevEl: '.card-button-prev',
                    },
                    pagination: {
                        el: '.cards-fraction',
                        type: 'fraction',
                        renderFraction: function (currentClass, totalClass) {
                            return '<span class="' + currentClass + '"></span> of <span class="' + totalClass + '"></span>';
                        }
                    },
                    breakpoints: {
                        1200: { slidesPerView: 4 },
                        900: { slidesPerView: 3 },
                        600: { slidesPerView: 2 },
                    }
                });

                 const slides = document.querySelectorAll('.wedding-cards-swiper .swiper-slide');
    const addCardBtn = document.getElementById('add-wedding-card-to-cart');
    slides.forEach(slide => {
        slide.addEventListener('click', function() {
            slides.forEach(s => s.classList.remove('card-active'));
            this.classList.add('card-active');
            // Enable the Add Card button
            if (addCardBtn) {
                addCardBtn.classList.remove('disabled_btn');
            }
        });
    });
            }

                }
                
                // Update the total price to include the card
                this.updateTotal();
                
                console.log('✅ Wedding card selection restored successfully');
            } else {
                console.log('⚠️ Could not find matching wedding card in popup');
            }
        }, 500); // Wait for DOM to be ready
    },

    // Setup cleanup when navigating away from page
    setupNavigationCleanup() {
        // Clear localStorage when user navigates away from the page
        window.addEventListener('beforeunload', () => {
            localStorage.removeItem('editingHamperData');
            console.log('🗑️ Cleared editing hamper data on page unload');
        });

        // Also clear when user clicks on navigation links
        document.addEventListener('click', (e) => {
            const link = e.target.closest('a');
            if (link && link.href && link.href !== window.location.href) {
                // If it's a navigation link to a different page, clear the data
                if (!link.href.includes('#') && link.href.includes(window.location.origin)) {
                    localStorage.removeItem('editingHamperData');
                    console.log('🗑️ Cleared editing hamper data on navigation click');
                }
            }
        });

        // Clear localStorage if user manually changes URL
        window.addEventListener('popstate', () => {
            localStorage.removeItem('editingHamperData');
            console.log('🗑️ Cleared editing hamper data on history change');
        });
    },

    // Cache DOM elements for performance
    cacheDOMElements() {
        this.elements = {
            // Steps
            steps: document.querySelectorAll('.hamper-step'),
            stepContents: document.querySelectorAll('.step-content'),

            // Step 1: Box Selection
            boxOptions: document.querySelectorAll('.box-option'),
            continueToItems: document.getElementById('continue-to-items'),

            // Step 3: Personalise
            personalisationItems: document.getElementById('personalisation-items'),
            continueToMessaging: document.getElementById('continue-to-messaging'),

            // Step 4: Messaging
            giftTagToggle: document.getElementById('gift-tag-toggle'),
            giftTagFields: document.getElementById('gift-tag-fields'),
            giftTagTo: document.getElementById('gift-tag-to'),
            giftTagFrom: document.getElementById('gift-tag-from'),
            giftMessage: document.getElementById('gift-message'),
            messageCount: document.getElementById('message-count'),
            luxuryBoxMessaging: document.getElementById('luxury-box-messaging'),
            boxPersonalisation: document.getElementById('box-personalisation'),
            boxMessageFields: document.getElementById('box-message-fields'),
            boxInsideMessage: document.getElementById('box-inside-message'),
            boxOutsideMessage: document.getElementById('box-outside-message'),
            insideCount: document.getElementById('inside-count'),
            outsideCount: document.getElementById('outside-count'),
            addToBasket: document.getElementById('add-to-basket'),
            summaryBoxName: document.getElementById('summary-box-name'),
            summaryEditBox: document.getElementById('summary-edit-box'),
            summaryItemsCount: document.getElementById('summary-items-count'),
            summaryTotalPrice: document.getElementById('summary-total-price'),
            addToBasketSummary: document.getElementById('add-to-basket-summary'),

            // Sidebar
            hamperSidebar: document.getElementById('hamper-sidebar'),
            hamperSidebarOverlay: document.getElementById('hamper-sidebar-overlay'),
            sidebarBoxName: document.getElementById('sidebar-box-name'),
            sidebarItemsList: document.getElementById('sidebar-items-list'),
            sidebarItemsCount: document.getElementById('sidebar-items-count'),
            hamperTotalPrice: document.getElementById('hamper-total-price'),
            editBox: document.getElementById('edit-box'),
            sidebarContinue: document.getElementById('sidebar-continue'),
            hamperSidebarClose: document.getElementById('hamper-sidebar-close'),

            // Navigation
            backButtons: document.querySelectorAll('.btn-back, .step-back-button'),

            // Loading
            loadingSpinner: document.querySelector('.loading'),

            // Mobile sticky bar
            mobileStickyBar: document.getElementById('mobile-sticky-bar'),
            mobileViewEdit: document.getElementById('mobile-view-edit'),
            mobileContinueBtn: document.getElementById('mobile-continue-btn'),
            mobileItemsCount: document.getElementById('mobile-items-count'),
            mobileTotalPrice: document.getElementById('mobile-total-price'),

            // Desktop sticky bar
            desktopStickyBar: document.getElementById('desktop-sticky-bar'),
            desktopViewEdit: document.getElementById('desktop-view-edit'),
            desktopContinueBtn: document.getElementById('desktop-continue-btn'),
            desktopItemsCount: document.getElementById('desktop-items-count'),
            desktopTotalPrice: document.getElementById('desktop-total-price')
        };
    },

    // Bind all events
    bindEvents() {
        // Box selection
        this.elements.boxOptions.forEach(box => {
            box.addEventListener('click', (e) => this.selectBox(e));
        });

        // Continue to Personalise (skip Choose Items)
        if (this.elements.continueToItems) {
            this.elements.continueToItems.addEventListener('click', () => {
                // Products are already loaded in selectedItems from init()
                this.goToStep(2);
                this.updateSidebarItems();
                this.updateTotal();
            });
        }
        if (this.elements.continueToMessaging) {
            this.elements.continueToMessaging.addEventListener('click', () => this.goToStep(3));
        }

        // Back buttons
        this.elements.backButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const targetStep = parseInt(e.currentTarget.getAttribute('data-go-to-step'));
                this.goToStep(targetStep);
            });
        });

        // Gift tag and messaging
        if (this.elements.giftTagToggle) {
            
            this.elements.giftTagToggle.addEventListener('change', (e) => {
                this.state.messaging.giftTag = e.target.checked;
                this.elements.giftTagFields.classList.toggle('active', e.target.checked);

                // Set required attribute on fields if toggle is on
                this.elements.giftTagTo.required = e.target.checked;
                this.elements.giftTagFrom.required = e.target.checked;
                this.elements.giftMessage.required = e.target.checked;
            });

            this.elements.giftTagToggle.click(); // Ensure toggle is clickable
        }

        if (this.elements.giftMessage) {
            this.elements.giftMessage.addEventListener('input', (e) => {
                this.state.messaging.giftMessage = e.target.value;
                this.elements.messageCount.textContent = e.target.value.length;
            });
        }

        // Box personalisation
        if (this.elements.boxPersonalisation) {
            this.elements.boxPersonalisation.addEventListener('change', (e) => {
                const value = e.target.value;
                this.state.messaging.boxPersonalisation = value;

                if (value) {
                    this.elements.boxMessageFields.style.display = 'block';

                    // Show/hide appropriate fields
                    if (value === 'inside-outside') {
                        document.getElementById('inside-message-group').style.display = 'block';
                        document.getElementById('outside-message-group').style.display = 'block';
                    } else if (value === 'front-only') {
                        document.getElementById('inside-message-group').style.display = 'none';
                        document.getElementById('outside-message-group').style.display = 'block';
                    }
                    else{
                         document.getElementById('inside-message-group').style.display = 'none';
                        document.getElementById('outside-message-group').style.display = 'none';
                    }

                    this.updateTotal();
                } else {
                    this.elements.boxMessageFields.style.display = 'none';
                   
                }
            });
        }

        // Summary section events
        if (this.elements.summaryEditBox) {
            this.elements.summaryEditBox.addEventListener('click', () => {
                this.goToStep(1);
            });
        }

        if (this.elements.addToBasketSummary) {
            this.elements.addToBasketSummary.addEventListener('click', () => this.submitHamper());
        }

        // Character counters
        if (this.elements.boxInsideMessage) {
            this.elements.boxInsideMessage.addEventListener('input', (e) => {
                this.state.messaging.boxInsideMessage = e.target.value;
                this.elements.insideCount.textContent = e.target.value.length;
            });
        }
        if (this.elements.boxOutsideMessage) {
            this.elements.boxOutsideMessage.addEventListener('input', (e) => {
                this.state.messaging.boxOutsideMessage = e.target.value;
                this.elements.outsideCount.textContent = e.target.value.length;
            });
        }

        // Sidebar
        if (this.elements.editBox) {
            this.elements.editBox.addEventListener('click', () => {
                this.goToStep(1);
            });
        }
        if (this.elements.sidebarContinue) {
            this.elements.sidebarContinue.addEventListener('click', () => {
                this.goToStep(3);
            });
        }
        if (this.elements.hamperSidebarClose) {
            this.elements.hamperSidebarClose.addEventListener('click', () => this.closeSidebar());
        }
        if (this.elements.hamperSidebarOverlay) {
            this.elements.hamperSidebarOverlay.addEventListener('click', () => this.closeSidebar());
        }

        // Mobile sticky bar events
        if (this.elements.mobileViewEdit) {
            this.elements.mobileViewEdit.addEventListener('click', () => this.openSidebar());
        }
        if (this.elements.mobileContinueBtn) {
            this.elements.mobileContinueBtn.addEventListener('click', () => {
                this.goToStep(2);
            });
        }

        // Desktop sticky bar events
        if (this.elements.desktopViewEdit) {
            this.elements.desktopViewEdit.addEventListener('click', () => this.openSidebar());
        }
        if (this.elements.desktopContinueBtn) {
            this.elements.desktopContinueBtn.addEventListener('click', () => {
                this.goToStep(2);
            });
        }

        // Add to basket
        if (this.elements.addToBasket) {
            this.elements.addToBasket.addEventListener('click', () => this.submitHamper());
        }

        // Back to product button
        const backToProductBtn = document.querySelector('.back-to-product');
        if (backToProductBtn) {
            backToProductBtn.addEventListener('click', () => {
                if (this.state.currentStep > 1) {
                    this.goToStep(this.state.currentStep - 1);
                }
            });
        }

        // Make completed steps clickable
        document.querySelectorAll('.hamper-step.completed').forEach(step => {
            step.addEventListener('click', (e) => {
                const stepNumber = parseInt(step.getAttribute('data-step'));
                if (!isNaN(stepNumber)) {
                    this.goToStep(stepNumber);
                }
            });
        });


    },

    // Box selection
    selectBox(e) {
        const boxOption = e.currentTarget;
        const boxType = boxOption.getAttribute('data-box-type');
        const boxPrice = parseFloat(boxOption.getAttribute('data-box-price'));

        // Remove previous selection
        this.elements.boxOptions.forEach(box => box.classList.remove('selected'));

        // Add selection
        boxOption.classList.add('selected');

        // Update state
        this.state.selectedBox = {
            type: boxType,
            price: boxPrice,
            name: boxOption.querySelector('.box-option-title').textContent
        };

        // Handle color selection for wicker basket
        if (boxType === 'wicker-basket') {
            const colorSelect = boxOption.querySelector('.box-color');
            this.state.selectedBox.color = colorSelect.value;
            this.updateBoxImage(boxOption, boxType, colorSelect.value);

            // Remove existing event listeners to prevent duplicates
            const existingListener = colorSelect._changeHandler;
            if (existingListener) {
                colorSelect.removeEventListener('change', existingListener);
            }

            // Add new event listener
            const changeHandler = (e) => {
                this.state.selectedBox.color = e.target.value;
                this.updateBoxImage(boxOption, boxType, e.target.value);
                this.updateSidebarBox();
            };

            colorSelect._changeHandler = changeHandler;
            colorSelect.addEventListener('change', changeHandler);
        } else {
            this.updateBoxImage(boxOption, boxType);
        }

        // Immediately go to personalisation step (skip items step)
        this.elements.continueToItems.disabled = false;
        this.updateSidebarBox();
    },

    // New method to handle image updates
    updateBoxImage(boxOption, boxType, color = null) {
        const img = boxOption.querySelector('.box-option-image img');
        let imageSrc;
       

        if (boxType === 'wicker-basket') {
            imageSrc = window.boxImages['wicker-basket'][color || 'light'];
        } else if (boxType === 'luxury-box') {
             
            imageSrc = window.boxImages['luxury-box'];
            console.log('Luxury box image source:', imageSrc);
            
        }

        if (imageSrc && img) {
            img.src = imageSrc;
            img.alt = this.state.selectedBox.name + (color ? ` (${color})` : '');
        }
    },

    // Navigation between steps
    goToStep(stepNumber) {
        this.state.currentStep = stepNumber;

        // Hide sidebar and sticky bars for all steps
        this.closeSidebar();
        if (this.elements.mobileStickyBar) {
            this.elements.mobileStickyBar.style.display = 'none';
        }
        if (this.elements.desktopStickyBar) {
            this.elements.desktopStickyBar.classList.remove('show');
        }

        // Update step indicators
        this.elements.steps.forEach((step, index) => {
            if (index < stepNumber - 1) {
                step.classList.add('completed');
                step.classList.remove('active');
            } else if (index === stepNumber - 1) {
                step.classList.add('active');
                step.classList.remove('completed');
            } else {
                step.classList.remove('active', 'completed');
            }
        });

        // Show correct content
        this.elements.stepContents.forEach(content => {
            const contentStep = parseInt(content.getAttribute('data-step-content'));
            content.classList.toggle('active', contentStep === stepNumber);
        });

        // Step-specific logic
        if (stepNumber === 2) {
            this.setupPersonalisation();
            
            // Auto-populate personalisation if we have pending editing data
            if (this.state.pendingEditingData && this.state.pendingEditingData.personalisations['Your message']) {
                console.log('🔄 User navigated to step 2, auto-populating personalisation...');
                setTimeout(() => {
                    this.autoPopulatePersonalisationData(this.state.pendingEditingData);
                }, 500);
            }
        } else if (stepNumber === 3) {
            this.setupMessaging();
            
            // Update button text based on editing mode
            this.updateButtonText();
            
            // Auto-populate messaging if we have pending editing data
            if (this.state.pendingEditingData) {
                console.log('🔄 User navigated to step 3, auto-populating messaging...');
                setTimeout(() => {
                    this.autoPopulateMessagingData(this.state.pendingEditingData);
                }, 500);
            }
        }
console.log('Navigated to step', stepNumber);
        // Show/hide back-to-product button
        const backToProductBtn = document.querySelector('.back-to-product');
        if (backToProductBtn) {
            backToProductBtn.style.display = (stepNumber > 1) ? 'block' : 'none';
        }

        // Re-attach click listeners to completed steps
        document.querySelectorAll('.hamper-step.completed').forEach(step => {
            // Remove previous listener to prevent duplicates
            step.onclick = null;
            step.addEventListener('click', (e) => {
                const stepNumber = parseInt(step.getAttribute('data-step'));
                if (!isNaN(stepNumber)) {
                    this.goToStep(stepNumber);
                }
            });
        });

        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    openSidebar() {
        this.elements.hamperSidebar.classList.add('active');
        if (window.innerWidth < 750) {
            if (!this.elements.hamperSidebarOverlay) {
                const overlay = document.createElement('div');
                overlay.className = 'hamper-sidebar-overlay';
                overlay.id = 'hamper-sidebar-overlay';
                document.body.appendChild(overlay);
                this.elements.hamperSidebarOverlay = overlay;
                overlay.addEventListener('click', () => this.closeSidebar());
            }
            this.elements.hamperSidebarOverlay.classList.add('active');
            document.body.classList.add('hamper-sidebar-open');
        }
    },

    closeSidebar() {
        this.elements.hamperSidebar.classList.remove('active');
        if (this.elements.hamperSidebarOverlay) {
            this.elements.hamperSidebarOverlay.classList.remove('active');
        }
        document.body.classList.remove('hamper-sidebar-open');
    },

    // Update sidebar box info
    updateSidebarBox() {
        if (this.state.selectedBox) {
            let boxName = this.state.selectedBox.name;
            if (this.state.selectedBox.color) {
                boxName += ` (${this.state.selectedBox.color.charAt(0).toUpperCase() + this.state.selectedBox.color.slice(1)})`;
            }
            this.elements.sidebarBoxName.textContent = boxName;
        } else {
            this.elements.sidebarBoxName.textContent = 'No box selected';
        }
        this.updateTotal();
        this.updateSummarySection();
    },

    // Update sidebar items
    updateSidebarItems() {
        this.elements.sidebarItemsList.innerHTML = '';
        this.state.selectedItems.forEach(item => {
            const itemEl = document.createElement('div');
            itemEl.className = 'hamper-sidebar-item';
            itemEl.innerHTML = `
            <div class="hamper-sidebar-item-image">
                <img src="${item.images[0]?.src || ''}" alt="${item.title}">
            </div>
            <div class="hamper-sidebar-item-content">
                <div class="hamper-sidebar-item-title">${item.title}</div>
                <div class="hamper-sidebar-item-price">£${item.price.toFixed(2)}</div>
            </div>
        `;
            this.elements.sidebarItemsList.appendChild(itemEl);
        });

        // Update count
        const totalItems = this.state.selectedItems.reduce((sum, item) => sum + item.quantity, 0);
        const countElement = this.elements.sidebarItemsCount;
        const newCount = `(${totalItems} items)`;
        countElement.textContent = newCount;

        this.updateTotal();
        this.updateSummarySection();
    },

    // Update total price
 updateTotal() {
    let total = 0;

    // Get the main product price from the DOM
    const mainProductPriceEl = document.querySelector('.price-item.price-item--last');
    if (mainProductPriceEl) {
        // Find the first price value inside (strip currency and text)
        const priceText = mainProductPriceEl.textContent.replace(/[^\d.,]/g, '').replace(',', '');
        const mainProductPrice = parseFloat(priceText);
        if (!isNaN(mainProductPrice)) {
            total += mainProductPrice;
        }
    }

    // Add box price if selected
    if (this.state.selectedBox) {
        total += this.state.selectedBox.price;
    }

    // Add box personalisation price if selected
    if (this.state.messaging.boxPersonalisation && this.elements.boxPersonalisation) {
        const option = this.elements.boxPersonalisation.querySelector(`option[value="${this.state.messaging.boxPersonalisation}"]`);
        if (option) {
            console.log('Adding box personalisation price:', option.getAttribute('data-price'));
            total += parseFloat(option.getAttribute('data-price') || 0);
            console.log('New total after box personalisation:', total);
        }
    }

    // Add personalisation prices
    Object.values(this.state.personalisations).forEach(personalisation => {
        total += personalisation.price || 0;
    });

    // Add prices of all selected items (cards/products from popup)
    const selectedCard = document.querySelector('.wedding-cards-swiper .swiper-slide.card-active');
if (selectedCard) {
    const price = parseFloat(selectedCard.getAttribute('data-price')) || 0;
    total += price;

}

    // Update all total price elements
    if (this.elements.hamperTotalPrice) {
        this.elements.hamperTotalPrice.textContent = `£${total.toFixed(2)}`;
    }
    if (this.elements.summaryTotalPrice) {
        this.elements.summaryTotalPrice.textContent = `£${total.toFixed(2)}`;
    }
    if (this.elements.mobileTotalPrice) {
        this.elements.mobileTotalPrice.textContent = `£${total.toFixed(2)}`;
    }
    if (this.elements.desktopTotalPrice) {
        this.elements.desktopTotalPrice.textContent = `£${total.toFixed(2)}`;
    }
},

    // Setup personalisation step
    setupPersonalisation() {
        this.elements.personalisationItems.innerHTML = '';
        const personalisableItems = this.state.selectedItems.filter(item => item.tags.some(tag => tag.toLowerCase().includes('personalise:')));
        
        if (personalisableItems.length === 0) {
            this.elements.personalisationItems.innerHTML = '<div class="no-personalisation-message">You have selected no items that can be personalised, please proceed to messaging</div>';
            return;
        }
        const info = document.createElement('p');
        info.textContent = 'You have selected items that can be personalised, please provide details here for each item';
        this.elements.personalisationItems.appendChild(info);
        personalisableItems.forEach(item => {
            
            for (let i = 0; i < item.quantity; i++) {
                console.log('I am here: ', item.personalisationType);
                const itemEl = document.createElement('div');
                itemEl.className = 'personalisation-item';
                const itemKey = `${item.id}_${i}`;
                const personalisationType = item.personalisationType;
                if (!personalisationType) continue;
                itemEl.innerHTML = `
          <div class="personalisation-item-image">
            <img src="${item.images[0]?.src || ''}" alt="${item.title}">
            <h3 class="mobile-personalisation-title">${item.title}</h3>
          </div>
          
          <div class="personalisation-content">
            <h3 class="personalisation-title">${item.title}</h3>
            <div class="personalisation-toggle">
              <span>${personalisationType.question}</span>
              <label class="toggle-switch">
                <input type="checkbox" data-item-key="${itemKey}">
                <span class="toggle-slider"></span>
              </label>
            </div>
            <div class="personalisation-options" data-item-key="${itemKey}">
              <div class="personalisation-field">
              <div class="label-plus-price">
                <label>${personalisationType.label}</label>
                <span class="personalisation-price">(+£${personalisationType.price.toFixed(2)})</span>
                </div>
                <input type="text" 
                       placeholder="${personalisationType.placeholder}" 
                       maxlength="${personalisationType.maxLength}" 
                       data-item-key="${itemKey}">
                <span class="character-count"><span>0</span>/${personalisationType.maxLength}</span>
              </div>
            </div>
          </div>
        `;
                const toggle = itemEl.querySelector('input[type="checkbox"]');
                const options = itemEl.querySelector('.personalisation-options');
                const input = itemEl.querySelector('input[type="text"]');
                const charCount = itemEl.querySelector('.character-count span');
                if (this.state.personalisations[itemKey]) {
                    toggle.checked = true;
                    options.classList.add('active');
                    input.value = this.state.personalisations[itemKey].value;
                    charCount.textContent = input.value.length;
                }
                toggle.addEventListener('change', (e) => {
                    options.classList.toggle('active', e.target.checked);
                    if (!e.target.checked) {
                        delete this.state.personalisations[itemKey];
                        input.value = '';
                        charCount.textContent = '0';
                    }
                    this.updateTotal();
                });
                input.addEventListener('input', (e) => {
                    charCount.textContent = e.target.value.length;
                    if (e.target.value) {
                        this.state.personalisations[itemKey] = {
                            productId: item.id,
                            type: personalisationType.type,
                            label: personalisationType.label,
                            value: e.target.value,
                            price: personalisationType.price
                        };
                    } else {
                        delete this.state.personalisations[itemKey];
                    }
                    this.updateTotal();
                });
                this.elements.personalisationItems.appendChild(itemEl);
            }
        });
    },

    // Setup messaging step
    setupMessaging() {
        if (this.state.selectedBox && this.state.selectedBox.type === 'luxury-box') {
            this.elements.luxuryBoxMessaging.style.display = 'block';
        } else {

            this.elements.luxuryBoxMessaging.style.display = 'none';
            const boxInsideMessageInput = document.getElementById('box-inside-message');
            if (boxInsideMessageInput) {
    boxInsideMessageInput.value = '';
}
         const boxOutsideMessageInput = document.getElementById('box-outside-message');
            if (boxOutsideMessageInput) {
    boxOutsideMessageInput.value = '';
}
        }
        this.updateSummarySection();
    },

    // Update the summary section
    updateSummarySection() {
        if (this.elements.summaryBoxName) {
            if (this.state.selectedBox) {
                let boxName = this.state.selectedBox.name;
                if (this.state.selectedBox.color) {
                    boxName += ` (${this.state.selectedBox.color.charAt(0).toUpperCase() + this.state.selectedBox.color.slice(1)})`;
                }
                this.elements.summaryBoxName.textContent = boxName;
            } else {
                this.elements.summaryBoxName.textContent = 'No box selected';
            }
        }
        if (this.elements.summaryItemsCount) {
            const totalItems = this.state.selectedItems.reduce((sum, item) => sum + item.quantity, 0);
            this.elements.summaryItemsCount.textContent = `${totalItems} Selected Items`;
        }
        this.updateSummaryTotal();
    },

    // Update summary total
    updateSummaryTotal() {
    if (!this.elements.summaryTotalPrice) return;
    let total = 0;

    // Get the main product price from the DOM
    const mainProductPriceEl = document.querySelector('.price-item.price-item--last');
    if (mainProductPriceEl) {
        const priceText = mainProductPriceEl.textContent.replace(/[^\d.,]/g, '').replace(',', '');
        const mainProductPrice = parseFloat(priceText);
        if (!isNaN(mainProductPrice)) {
            total += mainProductPrice;
        }
    }

    // Add box price if selected
    if (this.state.selectedBox) {
        total += this.state.selectedBox.price;
    }

    // Add box personalisation price if selected
    if (this.state.messaging.boxPersonalisation && this.elements.boxPersonalisation) {
        const option = this.elements.boxPersonalisation.querySelector(`option[value="${this.state.messaging.boxPersonalisation}"]`);
        if (option) {
            total += parseFloat(option.getAttribute('data-price') || 0);
        }
    }

    // Add personalisation prices
    Object.values(this.state.personalisations).forEach(personalisation => {
        total += personalisation.price || 0;
    });

        // Add prices of all selected items (cards/products from popup)
    const selectedCard = document.querySelector('.wedding-cards-swiper .swiper-slide.card-active');
if (selectedCard) {
    const price = parseFloat(selectedCard.getAttribute('data-price')) || 0;
    total += price;

}

    this.elements.summaryTotalPrice.textContent = `£${total.toFixed(2)}`;
},

    // Update button text based on editing mode
    updateButtonText() {
        const buttonText = this.state.isEditingMode ? 'Update Hamper' : 'Add to Basket';
        
        // Update both Add to Basket buttons
        if (this.elements.addToBasket) {
            this.elements.addToBasket.textContent = buttonText;
        }
        if (this.elements.addToBasketSummary) {
            // Find the text content, preserve the loading spinner
            const loadingSpinner = this.elements.addToBasketSummary.querySelector('.loading');
            this.elements.addToBasketSummary.innerHTML = buttonText;
            if (loadingSpinner) {
                this.elements.addToBasketSummary.appendChild(loadingSpinner);
            }
        }
        
        console.log(`🔄 Button text updated to: "${buttonText}"`);
    },

    // Submit hamper to cart
async submitHamper() {
    // Show loading on both buttons
    const loadingSpinners = document.querySelectorAll('.btn-add-to-basket .loading, .btn-add-to-basket-summary .loading');
    loadingSpinners.forEach(spinner => {
        if (spinner) spinner.style.display = 'inline-block';
    });

    // Disable both buttons
    if (this.elements.addToBasket) this.elements.addToBasket.disabled = true;
    if (this.elements.addToBasketSummary) this.elements.addToBasketSummary.disabled = true;

     // --- Move validation here ---
    if (this.state.messaging.giftTag) {
        const to = this.elements.giftTagTo.value.trim();
        const from = this.elements.giftTagFrom.value.trim();
        const message = this.elements.giftMessage.value.trim();

        if (!to || !from || !message) {
            alert('Please fill in all gift tag fields (To, From, and Message).');
            loadingSpinners.forEach(spinner => { if (spinner) spinner.style.display = 'none'; });
            if (this.elements.addToBasket) this.elements.addToBasket.disabled = false;
            if (this.elements.addToBasketSummary) this.elements.addToBasketSummary.disabled = false;
            return;
        }
    }
    // --- End validation ---

    try {
        // If in editing mode, first remove the original hamper from cart
        if (this.state.isEditingMode && this.state.originalHamperId) {
            console.log('🗑️ Removing original hamper from cart:', this.state.originalHamperId);
            await this.removeOriginalHamperFromCart(this.state.originalHamperId);
        }
        // Get main product variant ID from data attribute
        const appDiv = document.getElementById('build-your-hamper');
        const mainProductId = appDiv ? appDiv.getAttribute('data-main-product-id') : null;
        
        console.log('Main product ID from DOM:', mainProductId);
        
        if (!mainProductId || isNaN(parseInt(mainProductId))) {
            throw new Error('Main product variant ID not found or invalid.');
        }

        // Build properties object for main product
        const properties = {};

        // Generate a unique hamper ID for this submission
        const hamperId = 'hamper_' + Date.now() + '_' + Math.random().toString(36).substring(2, 8);
        console.log('🆔 Generated new hamper ID:', hamperId);

        // Add hamper identification properties
        properties['_isHamperMain'] = 'true';
        properties['_hamperId'] = hamperId;
        
        console.log('📝 Main product properties:', properties);

        // Add box selection as a property (keep this)
        if (this.state.selectedBox) {
            properties['Box Selection'] = this.state.selectedBox.name + (this.state.selectedBox.color ? ` (${this.state.selectedBox.color})` : '');
        }

        // Add personalisation fields as properties
        Object.values(this.state.personalisations).forEach(personalisation => {
            properties[personalisation.label] = personalisation.value;
        });

        // Add messaging fields as properties (only if they have values)
        if (this.state.messaging.giftTag) {
            const giftTagTo = this.elements.giftTagTo.value.trim();
            const giftTagFrom = this.elements.giftTagFrom.value.trim();
            const giftMessage = this.elements.giftMessage.value.trim();
            
            if (giftTagTo) properties['Gift Tag To'] = giftTagTo;
            if (giftTagFrom) properties['Gift Tag From'] = giftTagFrom;
            if (giftMessage) properties['Gift Message'] = giftMessage;
        }

        // Only add box personalisation if the luxury-box-messaging section is visible
        const luxuryBoxMessaging = document.getElementById('luxury-box-messaging');
        const isLuxuryBoxMessagingVisible = luxuryBoxMessaging && luxuryBoxMessaging.style.display !== 'none' && luxuryBoxMessaging.offsetParent !== null;
        
        if (this.state.messaging.boxPersonalisation && isLuxuryBoxMessagingVisible) {
            properties['Box Personalisation'] = this.state.messaging.boxPersonalisation;
            
            const boxInsideMessage = (this.elements.boxInsideMessage && this.elements.boxInsideMessage.value.trim()) || '';
            const boxOutsideMessage = (this.elements.boxOutsideMessage && this.elements.boxOutsideMessage.value.trim()) || '';
            
            // Only add messages if they have actual content
            if (boxInsideMessage) {
                properties['Box Inside Message'] = boxInsideMessage;
            }
            if (boxOutsideMessage) {
                properties['Box Outside Message'] = boxOutsideMessage;
            }
        }

        // Prepare items array for cart
        const items = [
            {
                id: parseInt(mainProductId),
                quantity: 1,
                properties: properties
            }
        ];

        // Add the selected box as a separate product
        if (this.state.selectedBox) {
    const boxVariantId = this.getBoxVariantId();
    if (boxVariantId) {
        const properties = {
            '_hamperLinked': 'true',
            '_hamperId': hamperId,
            'Hamper Type': 'Themed Hamper',
        };

        // Add box personalisation if selected and luxury-box-messaging is visible (only include messages with actual content)
        const luxuryBoxMessagingForBox = document.getElementById('luxury-box-messaging');
        const isLuxuryBoxMessagingVisibleForBox = luxuryBoxMessagingForBox && luxuryBoxMessagingForBox.style.display !== 'none' && luxuryBoxMessagingForBox.offsetParent !== null;
        
        if (this.state.messaging.boxPersonalisation && isLuxuryBoxMessagingVisibleForBox) {
            properties['Box Personalisation'] = this.state.messaging.boxPersonalisation === 'inside-outside'
                ? 'Messages Both Inside and Outside'
                : 'Message On the Box Front';
                
            const boxInsideMessage = (this.elements.boxInsideMessage && this.elements.boxInsideMessage.value.trim()) || '';
            const boxOutsideMessage = (this.elements.boxOutsideMessage && this.elements.boxOutsideMessage.value.trim()) || '';
            
            // Only add messages if they have actual content
            if (boxInsideMessage) {
                properties['Inside Message'] = boxInsideMessage;
            }
            if (boxOutsideMessage) {
                properties['Outside Message'] = boxOutsideMessage;
            }
        }

        // Add gift tag info (only if values exist)
        if (this.state.messaging.giftTag) {
            const giftTagTo = this.elements.giftTagTo ? this.elements.giftTagTo.value.trim() : '';
            const giftTagFrom = this.elements.giftTagFrom ? this.elements.giftTagFrom.value.trim() : '';
            const giftMessage = this.elements.giftMessage ? this.elements.giftMessage.value.trim() : '';
            
            if (giftTagTo) properties['Gift Tag To'] = giftTagTo;
            if (giftTagFrom) properties['Gift Tag From'] = giftTagFrom;
            if (giftMessage) properties['Gift Message'] = giftMessage;
        }

        // Add box type for clarity
        properties['Box Type'] = this.state.selectedBox.name + (this.state.selectedBox.color ? ` (${this.state.selectedBox.color})` : '');

        items.push({
            id: parseInt(boxVariantId),
            quantity: 1,
            properties: properties
        });
    }
}

        // Add personalisation:oxton as a separate product (ID: 10773350285646)
        Object.values(this.state.personalisations).forEach(personalisation => {
    if (personalisation.type === 'oxton' && personalisation.value) {
        if(personalisation.type === 'oxton'){
            var personalizationtype = 'Liqueur Bottle';
        }
        else{
            var personalizationtype = personalisation.type;
        }
        items.push({
            id: parseInt(54810509377870),
            quantity: 1,
            properties: {
                _builder: true,
                '_hamperLinked': 'true',
                '_hamperId': hamperId,
                'Personalisation Message': personalisation.value,
                'Personalisation Type': personalizationtype
            }
        });
    }
});

// Add box personalisation products based on selection and input (only if luxury-box-messaging is visible)
const luxuryBoxMessagingForProducts = document.getElementById('luxury-box-messaging');
const isLuxuryBoxMessagingVisibleForProducts = luxuryBoxMessagingForProducts && luxuryBoxMessagingForProducts.style.display !== 'none' && luxuryBoxMessagingForProducts.offsetParent !== null;

const boxPersonalisationValue = this.state.messaging.boxPersonalisation;
const boxInsideMessage = (this.elements.boxInsideMessage && this.elements.boxInsideMessage.value) || '';
const boxOutsideMessage = (this.elements.boxOutsideMessage && this.elements.boxOutsideMessage.value) || '';

// For 'front-only' - only add if outside message exists and luxury messaging is visible
if (
    boxPersonalisationValue === 'front-only' &&
    boxOutsideMessage.trim() !== '' &&
    isLuxuryBoxMessagingVisibleForProducts
) {
    const frontOnlyProperties = {
        _builder: true,
        '_hamperLinked': 'true',
        '_hamperId': hamperId,
        'Box Personalisation': 'Front Only'
    };
    
    // Only add the message property if it has content
    if (boxOutsideMessage.trim()) {
        frontOnlyProperties['Outside Message'] = boxOutsideMessage.trim();
    }
    
    items.push({
        id: parseInt(54819597549902), // Variant ID for front-only
        quantity: 1,
        properties: frontOnlyProperties
    });
}

// For 'inside-outside' - only add if both messages exist and luxury messaging is visible
if (
    boxPersonalisationValue === 'inside-outside' &&
    boxInsideMessage.trim() !== '' &&
    boxOutsideMessage.trim() !== '' &&
    isLuxuryBoxMessagingVisibleForProducts
) {
    const insideOutsideProperties = {
        _builder: true,
        '_hamperLinked': 'true',
        '_hamperId': hamperId,
        'Box Personalisation': 'Inside & Outside'
    };
    
    // Only add message properties if they have content
    if (boxInsideMessage.trim()) {
        insideOutsideProperties['Inside Message'] = boxInsideMessage.trim();
    }
    if (boxOutsideMessage.trim()) {
        insideOutsideProperties['Outside Message'] = boxOutsideMessage.trim();
    }
    
    items.push({
        id: parseInt(54819550888270), // Variant ID for inside-outside
        quantity: 1,
        properties: insideOutsideProperties
    });
}

// Add the currently active card from the popup to the cart if not already in selectedItems/items
const selectedCard = document.querySelector('.wedding-cards-swiper .swiper-slide.card-active');
if (selectedCard) {
    const variantId = selectedCard.getAttribute('variant-id');
    // Check if already in items array (by variantId)
    const alreadyInItems = items.some(item => item.id == variantId);
    if (!alreadyInItems && variantId && !isNaN(parseInt(variantId))) {
        const title = selectedCard.getAttribute('data-title') || '';
        const price = parseFloat(selectedCard.getAttribute('data-price')) || 0;
        const image = selectedCard.querySelector('img')?.src || '';
        
        console.log('🎴 Adding wedding card to cart:', {
            variantId: variantId,
            title: title,
            price: price,
            isWeddingCard: true
        });
        
        items.push({
            id: parseInt(variantId),
            quantity: 1,
            properties: {
                _builder: true,
                '_hamperLinked': 'true',
                '_hamperId': hamperId,
                'isWeddingCard': 'true', // This is the key property we were missing!
                'Product Title': title
            }
        });
    }
}

        // Add all selectedItems to cart - COMMENTED OUT FOR TESTING
        // For themed hampers, the items might already be included in the main product
        // Comment this out temporarily to test if main product + box works
        /*
        console.log('Selected items to process:', this.state.selectedItems);
        this.state.selectedItems.forEach((item, index) => {
            console.log(`Processing selected item ${index + 1}:`, item);
            
            // Skip if no valid ID
            if (!item.id || isNaN(parseInt(item.id))) {
                console.warn(`Skipping selected item ${index + 1} - invalid ID:`, item.id);
                return;
            }
            
            // Use the first variant ID if the item has variants
            let variantId = item.id;
            if (item.variants && item.variants.length > 0 && item.variants[0].id) {
                variantId = item.variants[0].id;
                console.log(`Using first variant ID for ${item.title}:`, variantId);
            }
            
            items.push({
                id: parseInt(variantId),
                quantity: item.quantity || 1,
                properties: {
                    '_hamperLinked': 'true',
                    '_hamperId': hamperId,
                    'Product Title': item.title
                }
            });
        });
        */

        console.log('📦 All items before validation:', items);

        // Filter out any invalid items before sending to cart
        const validItems = items.filter(item => {
            if (!item.id || isNaN(parseInt(item.id)) || parseInt(item.id) <= 0) {
                console.warn('❌ Skipping invalid item ID:', item);
                return false;
            }
            if (!item.quantity || item.quantity <= 0) {
                console.warn('❌ Skipping item with invalid quantity:', item);
                return false;
            }
            console.log('✅ Valid item:', item.id, item.properties && item.properties._hamperLinked ? '(linked)' : '(main)');
            return true;
        });

        console.log('📦 Valid items to add:', validItems.length);
        console.log('📦 Valid items details:', validItems);

        if (validItems.length === 0) {
            throw new Error('No valid items to add to cart');
        }

        console.log('🚀 Sending to cart/add.js:', { items: validItems });

        // Add to cart via AJAX
        fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({ items: validItems })
        })
        .then(response => {
            if (!response.ok) {
                return response.text().then(text => {
                    throw new Error(`HTTP error! status: ${response.status} - ${text}`);
                });
            }
            return response.json();
        })
        .then(cart => {
            // Log success message based on mode
            if (this.state.isEditingMode) {
                console.log('✅ Hamper updated successfully!');
            } else {
                console.log('✅ Hamper added to cart successfully!');
            }
            
            // Clear editing state
            this.state.isEditingMode = false;
            this.state.originalHamperId = null;
            this.state.editingHamperData = null;
            
            window.location.href = '/cart';
        })
        .catch(error => {
            const message = this.state.isEditingMode 
                ? 'There was an error updating the hamper in your cart.'
                : 'There was an error adding the hamper to your cart.';
            
            console.error('❌ Cart operation failed:', error);
            console.error('❌ Error details:', error.message);
            console.error('❌ Items attempted to add:', validItems);
            console.error('❌ Editing mode:', this.state.isEditingMode);
            console.error('❌ Original hamper ID:', this.state.originalHamperId);
            
            alert(message + '\n\nError: ' + error.message + '\n\nPlease check the console for more details.');
            
            loadingSpinners.forEach(spinner => {
                if (spinner) spinner.style.display = 'none';
            });
            if (this.elements.addToBasket) this.elements.addToBasket.disabled = false;
            if (this.elements.addToBasketSummary) this.elements.addToBasketSummary.disabled = false;
        });
    } catch (error) {
        // Provide specific error message based on editing mode
        let errorMessage = 'There was an error adding the hamper to your cart.';
        
        if (this.state.isEditingMode) {
            errorMessage = 'There was an error updating your hamper. Your original hamper remains in the cart.';
        }
        
        console.error('❌ Submit hamper failed:', error);
        console.error('❌ Error stack:', error.stack);
        console.error('❌ Current state:', {
            isEditingMode: this.state.isEditingMode,
            originalHamperId: this.state.originalHamperId,
            selectedBox: this.state.selectedBox,
            selectedItems: this.state.selectedItems.length,
            personalisations: Object.keys(this.state.personalisations).length
        });
        
        alert(errorMessage + '\n\nDetailed error: ' + error.message);
        
        loadingSpinners.forEach(spinner => {
            if (spinner) spinner.style.display = 'none';
        });
        if (this.elements.addToBasket) this.elements.addToBasket.disabled = false;
        if (this.elements.addToBasketSummary) this.elements.addToBasketSummary.disabled = false;
    }
},

    // Remove original hamper from cart when editing
    async removeOriginalHamperFromCart(hamperId) {
        try {
            console.log('🔍 Fetching current cart to find items with hamper ID:', hamperId);
            
            // First, get the current cart to find all items with the original hamper ID
            const cartResponse = await fetch('/cart.js');
            if (!cartResponse.ok) {
                throw new Error(`Failed to fetch cart: ${cartResponse.status} ${cartResponse.statusText}`);
            }
            const cart = await cartResponse.json();
            
            console.log('🛒 Current cart items:', cart.items.length);
            console.log('🛒 Cart items details:', cart.items.map(item => ({
                title: item.title,
                properties: item.properties,
                line: cart.items.indexOf(item) + 1
            })));
            
            // Find all cart items that belong to the original hamper
            const itemsToRemove = [];
            
            cart.items.forEach((item, index) => {
                // Check if item belongs to the original hamper
                if (
                    (item.properties && item.properties._hamperId === hamperId) ||
                    (item.properties && item.properties._isHamperMain === 'true' && item.properties._hamperId === hamperId) ||
                    (item.properties && item.properties._hamperLinked === 'true' && item.properties._hamperId === hamperId)
                ) {
                    itemsToRemove.push({
                        line: index + 1, // Shopify uses 1-indexed line numbers
                        key: item.key,
                        quantity: item.quantity,
                        title: item.title,
                        properties: item.properties
                    });
                }
            });
            
            console.log('🗑️ Found items to remove:', itemsToRemove.length);
            console.log('🗑️ Items details:', itemsToRemove);
            
            if (itemsToRemove.length === 0) {
                console.log('⚠️ No items found to remove for hamper ID:', hamperId);
                return;
            }
            
            // Remove items one by one, refetching cart each time to get correct line numbers
            let remainingItemsToRemove = [...itemsToRemove];
            
            while (remainingItemsToRemove.length > 0) {
                // Fetch current cart to get updated line numbers
                const updatedCartResponse = await fetch('/cart.js');
                if (!updatedCartResponse.ok) {
                    throw new Error(`Failed to fetch updated cart: ${updatedCartResponse.status}`);
                }
                const updatedCart = await updatedCartResponse.json();
                
                // Find the first item that still needs to be removed
                let itemToRemove = null;
                let currentLineNumber = null;
                
                for (let i = 0; i < updatedCart.items.length; i++) {
                    const cartItem = updatedCart.items[i];
                    
                    // Check if this cart item matches any of our items to remove
                    const matchingItem = remainingItemsToRemove.find(removeItem => 
                        removeItem.key === cartItem.key || 
                        (removeItem.title === cartItem.title && 
                         cartItem.properties && 
                         cartItem.properties._hamperId === hamperId)
                    );
                    
                    if (matchingItem) {
                        itemToRemove = matchingItem;
                        currentLineNumber = i + 1; // Shopify uses 1-indexed
                        break;
                    }
                }
                
                if (!itemToRemove) {
                    console.log('✅ No more items to remove - all items have been removed');
                    break;
                }
                
                console.log('🗑️ Removing item:', itemToRemove.title, 'Current line:', currentLineNumber);
                
                const updateResponse = await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({
                        line: currentLineNumber,
                        quantity: 0
                    })
                });
                
                if (!updateResponse.ok) {
                    const errorText = await updateResponse.text();
                    console.error('❌ Failed to remove item:', itemToRemove.title, 'Error:', errorText);
                    throw new Error(`Failed to remove item: ${itemToRemove.title} - ${errorText}`);
                }
                
                const result = await updateResponse.json();
                console.log('✅ Successfully removed item:', itemToRemove.title, 'Result:', result);
                
                // Remove this item from our tracking array
                remainingItemsToRemove = remainingItemsToRemove.filter(item => 
                    item.key !== itemToRemove.key
                );
                
                console.log('📋 Remaining items to remove:', remainingItemsToRemove.length);
            }
            
            console.log('✅ All original hamper items removed successfully');
            
        } catch (error) {
            console.error('❌ Error removing original hamper from cart:', error);
            console.error('❌ Error stack:', error.stack);
            throw error; // Re-throw to handle in submitHamper
        }
    },

    // Get box variant ID based on selection
    getBoxVariantId() {
        if (!window.boxVariantIds) {
            console.error('Box variant IDs not available. Please configure products in section settings.');
            return null;
        }

        let variantKey;
        if (this.state.selectedBox.type === 'wicker-basket') {
            variantKey = `${this.state.selectedBox.type}-${this.state.selectedBox.color}`;
        } else {
            variantKey = this.state.selectedBox.type;
        }

        const variantId = window.boxVariantIds[variantKey];

        if (!variantId) {
            console.error(`No variant ID found for: ${variantKey}`);
            return null;
        }

        console.log(`Box variant ID for ${variantKey}: ${variantId}`);
        return variantId;
    },

    // Update cart UI
    updateCartUI(cart) {
        // Update cart count in header
        const cartCountElements = document.querySelectorAll('.cart-count, .cart-count-bubble');
        cartCountElements.forEach(el => {
            el.textContent = cart.item_count;
            el.classList.remove('hidden');
        });

        // Trigger cart drawer if available
        if (typeof window.Shopify !== 'undefined' && window.Shopify.theme && window.Shopify.theme.openCartDrawer) {
            window.Shopify.theme.openCartDrawer();
        }
    },

    // Update UI
    updateUI() {
        this.updateSidebarBox();
        this.updateSidebarItems();
        this.updateTotal();
    },

    // Add this function to your BuildYourOwnHamper object:
    getPersonalisationType(tags) {
        const personalisationTypes = {
            'personalise:initials': {
                type: 'initials',
                question: 'Would you like this item personalised with initials?',
                label: 'Hot foil engraved initials (£4.00)',
                maxLength: 4,
                placeholder: 'Enter initials',
                price: 4.00
            },
            'personalise:bunnytoy': {
                type: 'bunnytoy',
                question: 'Would you like to personalise this item?',
                label: 'Add name or initials (£4.00)',
                maxLength: 15,
                placeholder: 'Enter name or initials',
                price: 4.00
            },
            'personalise:bottle': {
                type: 'bottle',
                question: 'Would you like to personalise your bottle?',
                label: 'Personalised message (£0.00)',
                maxLength: 15,
                placeholder: 'Enter message',
                price: 0.00
            },
            'personalise:oxton': {
                type: 'oxton',
                question: 'Would you like this bottle personalised with a message?',
                label: 'Your message',
                maxLength: 25,
                placeholder: 'Enter your message...',
                price: 2.00
            },
            'personalise:25': {
                type: '25',
                question: 'Would you like this item personalised with a message?',
                label: 'Your message (£4.00)',
                maxLength: 25,
                placeholder: 'Enter your message...',
                price: 4.00
            },
            'personalise:baby': {
                type: 'baby',
                question: 'Would you like this item personalised with a name?',
                label: "Baby's name (£4.00)",
                maxLength: 10,
                placeholder: "Enter baby's name...",
                price: 4.00
            },
            'personalise:card': {
                type: 'card',
                question: 'Would you like a complimentary hand written message to be included in your card?',
                label: 'Your message (£4.00)',
                maxLength: 100,
                placeholder: 'Enter your message...',
                price: 4.00
            },
            'personalise:chocribbon': {
                type: 'ribbon',
                question: 'Would you like to personalise this ribbon?',
                label: 'Your text (£4.00)',
                maxLength: 10,
                placeholder: 'Enter your text...',
                price: 4.00
            }
        };

        // Find the first tag that matches a key in personalisationTypes
        for (const tag of tags) {
            const tagLower = tag.toLowerCase();
            if (personalisationTypes[tagLower]) {
                return personalisationTypes[tagLower];
            }
        }
        return null;
    },
};

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    if (document.getElementById('build-your-hamper')) {
        BuildYourOwnHamper.init();
    }
});

// Listen for card selection event from the popup
// window.addEventListener('weddingCardSelected', function(e) {
//   const card = e.detail;
//   // Prevent duplicates
//   if (!BuildYourOwnHamper.state.selectedItems.some(item => item.id == card.id)) {
//     BuildYourOwnHamper.state.selectedItems.push({
//       id: card.id,
//       title: card.title,
//       price: card.price,
//       quantity: 1,
//       images: [{ src: card.image }],
//       tags: [],
//       vendor: '',
//       type: 'Card',
//       variants: [],
//     });
//     // Update UI and total
//     BuildYourOwnHamper.updateSidebarItems && BuildYourOwnHamper.updateSidebarItems();
//     BuildYourOwnHamper.updateTotal && BuildYourOwnHamper.updateTotal();
//     BuildYourOwnHamper.updateSummarySection && BuildYourOwnHamper.updateSummarySection();
//   }
// });

// Wedding Cards Popup logic
const toggle = document.querySelector('.toggle-switch.card-popup-toggle input');
const overlay = document.getElementById('wedding-cards-popup-overlay');
const closeBtn = document.getElementById('close-wedding-cards-popup');
const addCardBtn = document.getElementById('add-wedding-card-to-cart');

let weddingCardsSwiper = null;

if (toggle && overlay) {
    toggle.addEventListener('change', function() {
        if (toggle.checked) {
            overlay.style.display = 'flex';
            // Initialize Swiper only once
            if (!weddingCardsSwiper) {
                weddingCardsSwiper = new Swiper('.wedding-cards-swiper', {
                    slidesPerView: 2,
                    navigation: {
                        nextEl: '.card-button-next',
                        prevEl: '.card-button-prev',
                    },
                    pagination: {
                        el: '.cards-fraction',
                        type: 'fraction',
                        renderFraction: function (currentClass, totalClass) {
                            return '<span class="' + currentClass + '"></span> of <span class="' + totalClass + '"></span>';
                        }
                    },
                    breakpoints: {
                        1200: { slidesPerView: 4 },
                        900: { slidesPerView: 3 },
                        600: { slidesPerView: 2 },
                    }
                });
            }
            setTimeout(setupCardActiveHandler, 300); // Wait for DOM update
        }
        else {
            
    overlay.style.display = 'none';
    // Remove 'card-active' from all slides
    document.querySelectorAll('.wedding-cards-swiper .swiper-slide.card-active').forEach(slide => {
        slide.classList.remove('card-active');
    });
    // Disable the Add Card button
    const addCardBtn = document.getElementById('add-wedding-card-to-cart');
    if (addCardBtn) addCardBtn.classList.add('disabled_btn');
    updateSelectedCardWidget();

    BuildYourOwnHamper.updateSidebarItems && BuildYourOwnHamper.updateSidebarItems();
        BuildYourOwnHamper.updateTotal && BuildYourOwnHamper.updateTotal();
        BuildYourOwnHamper.updateSummarySection && BuildYourOwnHamper.updateSummarySection();
}
    });
}

if (closeBtn && overlay) {
    closeBtn.addEventListener('click', () => {
        overlay.style.display = 'none';
        // if (toggle) toggle.checked = false;
    });
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
            overlay.style.display = 'none';
            // if (toggle) toggle.checked = false;
        }
    });
}

function setupCardActiveHandler() {
    const slides = document.querySelectorAll('.wedding-cards-swiper .swiper-slide');
    const addCardBtn = document.getElementById('add-wedding-card-to-cart');
    slides.forEach(slide => {
        slide.addEventListener('click', function() {
            slides.forEach(s => s.classList.remove('card-active'));
            this.classList.add('card-active');
            // Enable the Add Card button
            if (addCardBtn) {
                addCardBtn.classList.remove('disabled_btn');
            }
        });
    });
    // Disable Add Card button initially
    if (addCardBtn) addCardBtn.classList.add('disabled_btn');
}

if (addCardBtn) {
    addCardBtn.addEventListener('click', function() {
        const selectedCard = document.querySelector('.wedding-cards-swiper .swiper-slide.card-active');
        if (!selectedCard) return;

        const variantId = selectedCard.getAttribute('variant-id');
        const title = selectedCard.getAttribute('data-title') || '';
        const price = parseFloat(selectedCard.getAttribute('data-price')) || 0;
        const image = selectedCard.querySelector('img')?.src || '';

        // Remove any existing card-type items
        BuildYourOwnHamper.state.selectedItems = BuildYourOwnHamper.state.selectedItems.filter(
            item => item.type !== 'Card'
        );
        // Add the new card
        BuildYourOwnHamper.state.selectedItems.push({
            id: variantId,
            title: title,
            price: price,
            quantity: 1,
            images: [{ src: image }],
            tags: [],
            vendor: '',
            type: 'Card',
            variants: [],
        });
        // Update UI and total
        BuildYourOwnHamper.updateSidebarItems && BuildYourOwnHamper.updateSidebarItems();
        BuildYourOwnHamper.updateTotal && BuildYourOwnHamper.updateTotal();
        BuildYourOwnHamper.updateSummarySection && BuildYourOwnHamper.updateSummarySection();

        // Close popup and reset UI
        overlay.style.display = 'none';

        updateSelectedCardWidget();
        // if (toggle) toggle.checked = false;
        // addCardBtn.classList.add('disabled_btn');
        // document.querySelectorAll('.wedding-cards-swiper .swiper-slide').forEach(s => s.classList.remove('card-active'));
    });
}

function updateSelectedCardWidget() {
    const widget = document.getElementById('selected-card-widget');
    if (!widget) return;

    // Get the currently active card from the popup
    const selectedCard = document.querySelector('.wedding-cards-swiper .swiper-slide.card-active');
    if (selectedCard) {
        const image = selectedCard.querySelector('img')?.src || '';
        const title = selectedCard.getAttribute('data-title') || '';
        widget.innerHTML = `
            <div class="selected-card-widget-inner" style="display:flex;">
                <div style="display:flex;align-items:flex-start;gap:10px;">
                    <img src="${image}" alt="${title}" style="width:48px;height:48px;object-fit:cover;">
                    <div>
                        <div class="selected-card-title">${title}</div>
                    </div>
                </div>
                <button class="edit-card-btn" style="">Edit</button>
            </div>
        `;
        // Edit button opens the popup again
        widget.querySelector('.edit-card-btn').onclick = function() {
            document.getElementById('wedding-cards-popup-overlay').style.display = 'flex';
        };
    } else {
        widget.innerHTML = '';
    }
}
